// Web Animations for the three tile changes a CSS transition can't express: a tile that
// didn't exist a frame ago (no start value), a tile React is about to unmount (no end
// state to transition to), and the promoted slot swapping its label text (the declared
// width doesn't change, only what it resolves to).
//
// `token()` resolves to a `var()` that `Element.animate` won't accept, so these mirror
// `motion.duration.long` and `motion.easing.out.bold` — the same pair the CSS
// transitions use, so every tile in one gesture moves on one curve.
const LONG_DURATION_MS = 250;
const OUT_BOLD_EASING = 'cubic-bezier(0, 0.4, 0, 1)';

// The row's gap (`space.075`). A zero-width tile would otherwise still hold its gap open,
// so the collapsed state cancels it and the row closes up smoothly instead of snapping
// 6px when the node finally unmounts.
const ROW_GAP_PX = 6;

const canAnimate = (element: HTMLElement): boolean =>
	typeof element.animate === 'function' &&
	!(
		typeof window !== 'undefined' &&
		typeof window.matchMedia === 'function' &&
		window.matchMedia('(prefers-reduced-motion: reduce)').matches
	);

const collapsedFrame: Keyframe = {
	width: '0px',
	paddingInlineStart: '0px',
	paddingInlineEnd: '0px',
	marginInlineStart: `-${ROW_GAP_PX}px`,
};

const naturalFrame = (tile: HTMLElement): Keyframe => {
	const { paddingInlineStart, paddingInlineEnd } = getComputedStyle(tile);

	return {
		width: `${tile.getBoundingClientRect().width}px`,
		paddingInlineStart,
		paddingInlineEnd,
		marginInlineStart: '0px',
	};
};

/** Grows a newly mounted tile out of zero width. */
export const animateTileIn = (tile: HTMLElement): void => {
	if (!canAnimate(tile)) {
		return;
	}

	tile.animate([collapsedFrame, naturalFrame(tile)], {
		duration: LONG_DURATION_MS,
		easing: OUT_BOLD_EASING,
	});
};

/**
 * Collapses a tile to zero width and holds it there until it unmounts. Resolves once the
 * tile has collapsed, straight away if motion is off.
 */
export const animateTileOut = (tile: HTMLElement): Promise<void> => {
	if (!canAnimate(tile)) {
		return Promise.resolve();
	}

	const animation = tile.animate([naturalFrame(tile), collapsedFrame], {
		duration: LONG_DURATION_MS,
		easing: OUT_BOLD_EASING,
		fill: 'forwards',
	});

	return animation.finished.then(
		() => undefined,
		// Cancelled because the tile came back mid-exit — nothing left to wait for.
		() => undefined,
	);
};

// `getAnimations()` also returns the tile's CSS transitions (fill, colour, border), which
// cancelling would snap to their end state. Those are the ones with a `transitionProperty`.
const isCssTransitionOrAnimation = (animation: Animation): boolean =>
	'transitionProperty' in animation || 'animationName' in animation;

/**
 * Stops this module's in-flight grow or collapse on a tile, e.g. when the app is reselected
 * before its tile has gone, or an effect is cleaned up. Leaves CSS transitions alone.
 */
export const cancelTileAnimations = (tile: HTMLElement): void => {
	if (typeof tile.getAnimations === 'function') {
		tile
			.getAnimations()
			.filter((animation) => !isCssTransitionOrAnimation(animation))
			.forEach((animation) => animation.cancel());
	}
};

/**
 * The label's width at rest. Read from `scrollWidth` so it's the text's own width even
 * while an animation clips or squeezes the label, e.g. as its tile grows in.
 */
export const measureLabelWidth = (label: HTMLElement): number => label.scrollWidth;

/**
 * Animates the label between two widths at rest. Uses `width`, not `max-width`: a
 * max-width can only cap a label, never widen one, so it can't shrink one label into
 * another. No fill, so the label goes back to its own content width afterwards.
 */
export const animateLabelWidth = (label: HTMLElement, fromWidth: number, toWidth: number): void => {
	if (!canAnimate(label) || Math.abs(toWidth - fromWidth) < 0.5) {
		return;
	}

	label.animate([{ width: `${fromWidth}px` }, { width: `${toWidth}px` }], {
		duration: LONG_DURATION_MS,
		easing: OUT_BOLD_EASING,
	});
};
